import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from './ui/Spinner';
import { useApi } from '../hooks/useApi';
import { useRefetchOnFocus } from '../hooks/useRefetchOnFocus';

type PipelineStage =
  | 'planning'
  | 'implementing'
  | 'developer'
  | 'review'
  | 'ready-release'
  | 'pre-release'
  | 'pending-merge'
  | 'post-release'
  | 'completed';

interface PipelineSession {
  id: string;
  title: string;
  projectName?: string;
  stage: PipelineStage;
  stageDetail: string;
  updatedAt: string;
  cost?: number;
  hermesSessionId?: string;
  daedalusSessionId?: string;
  prometheusSessionId?: string;
}

const stageConfig: Record<PipelineStage, { label: string; color: string; bgColor: string }> = {
  planning: { label: 'Planning', color: 'text-primary', bgColor: 'bg-primary-light' },
  implementing: { label: 'Implementing', color: 'text-blue-600', bgColor: 'bg-blue-50' },
  developer: { label: 'Developer', color: 'text-amber-600', bgColor: 'bg-amber-50' },
  review: { label: 'In Review', color: 'text-yellow-700', bgColor: 'bg-yellow-50' },
  'ready-release': { label: 'Ready for Release', color: 'text-teal-600', bgColor: 'bg-teal-50' },
  'pre-release': { label: 'Pre-Release', color: 'text-orange-600', bgColor: 'bg-orange-50' },
  'pending-merge': { label: 'Pending Merge', color: 'text-purple-600', bgColor: 'bg-purple-50' },
  'post-release': { label: 'Post-Release', color: 'text-orange-600', bgColor: 'bg-orange-50' },
  completed: { label: 'Completed', color: 'text-green-600', bgColor: 'bg-green-50' },
};

/**
 * A dashboard-style, cross-project rollup of what's in flight for one agent — shown on
 * the Hermes/Daedalus pages before a project is picked, so you can see everything at a
 * glance. Reads the same /dashboard/pipeline feed, filtered to the given stages, and
 * stays live (polls + refreshes on tab focus).
 */
export function PipelineSummary({
  stages,
  title,
  emptyText,
}: {
  stages: PipelineStage[];
  title: string;
  emptyText: string;
}) {
  const { get } = useApi();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<PipelineSession[]>([]);
  const [loading, setLoading] = useState(true);
  const getRef = useRef(get);
  getRef.current = get;

  const fetchPipeline = useCallback(async () => {
    const r = await getRef.current<PipelineSession[]>('/dashboard/pipeline');
    if (r.success && r.data) setSessions(r.data);
    setLoading(false);
  }, []);
  useEffect(() => {
    fetchPipeline();
    const id = setInterval(fetchPipeline, 30_000);
    return () => clearInterval(id);
  }, [fetchPipeline]);
  useRefetchOnFocus(fetchPipeline);

  const relevant = sessions
    .filter((s) => stages.includes(s.stage))
    .sort((a, b) => (b.updatedAt || '').localeCompare(a.updatedAt || ''));

  const byProject = new Map<string, number>();
  relevant.forEach((s) => {
    const key = s.projectName ?? 'Unknown project';
    byProject.set(key, (byProject.get(key) ?? 0) + 1);
  });

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{title}</h3>
        {!loading && (
          <span className="text-xs text-gray-500">
            {relevant.length} active{relevant.length ? ' · pick a project below to drill in' : ''}
          </span>
        )}
      </div>
      {loading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : relevant.length === 0 ? (
        <p className="text-sm text-gray-500 bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-4">{emptyText}</p>
      ) : (
        <>
          {byProject.size > 1 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {[...byProject.entries()].map(([proj, n]) => (
                <span key={proj} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full font-medium">
                  {proj}: {n}
                </span>
              ))}
            </div>
          )}
          <div className="space-y-2">
            {relevant.map((s) => {
              const cfg = stageConfig[s.stage];
              const target = s.daedalusSessionId
                ? `/daedalus/${s.daedalusSessionId}`
                : s.prometheusSessionId
                  ? `/prometheus/${s.prometheusSessionId}`
                  : s.hermesSessionId
                    ? `/hermes/${s.hermesSessionId}`
                    : null;
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => target && navigate(target)}
                  disabled={!target}
                  className="w-full text-left bg-white rounded-2xl border border-slate-100 shadow-sm shadow-slate-200/50 p-3.5 transition-shadow enabled:hover:shadow-md enabled:hover:shadow-slate-200/60 disabled:cursor-default"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${cfg.bgColor} ${cfg.color} shrink-0`}>
                        {cfg.label}
                      </span>
                      {s.projectName && <span className="text-xs text-gray-500 shrink-0">{s.projectName}</span>}
                      <span className="text-sm text-gray-800 font-medium truncate">{s.title}</span>
                    </div>
                    <span className="text-xs text-gray-500 shrink-0">{new Date(s.updatedAt).toLocaleDateString()}</span>
                  </div>
                  {s.stageDetail && <p className="text-xs text-gray-500 mt-1">{s.stageDetail}</p>}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
